import { useRouter } from 'next/router';
import React, { ComponentType, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import { useGlobalStore } from '../state/globalStore';
import { ENTITY_TYPE, LC_URL_QUERY_PARAMS, URL_QUERY_PARAMS } from './constants';
import { getValueFromQueryParam } from './helper';
import { reportErrorToPlayer } from './player-controller';
import { ERROR_CODES } from './player-controller/constants';
import { UserProps } from './types';

/**
 * Validates the entity type, entity id, base url etc.
 * Wrap your component in this HOC to be sure that baseurl, entity type, entity id is valid set correctly in zustand store
 */
function withValidation(WrappedComponent: ComponentType<UserProps>) {
  return function WithValidation(props: UserProps) {
    const windowType = typeof window;
    const entityData = useMemo(
      () => (windowType !== 'undefined' ? window['entityData'] : {}),
      [windowType],
    );
    const [errored, setErrored] = useState(false);
    const [validated, setValidated] = useState(false);
    const router = useRouter();

    useEffect(() => {
      /** If router is not ready, unlistedHash may return empty. */
      if (!entityData || !router.isReady) {
        return;
      }

      /**
       * In case of event, hash is present in /event/:eventId/:unlisted_hash
       * In case of video, hash is present in query param /leadcapture/video/:videoId?unlisted_hash=hash
       */
      const unlistedHash =
        (router.query['unlisted_hash'] as string | undefined) ||
        getValueFromQueryParam(URL_QUERY_PARAMS.UNLISTED_HASH) ||
        getValueFromQueryParam(LC_URL_QUERY_PARAMS.UNLISTED_HASH);

      const entityId = entityData['entity_id'];
      const eventId = entityData['event_id'];
      const apiEventId = eventId ? `${eventId}${unlistedHash ? `:${unlistedHash}` : ''}` : null;
      const apiEntityId = `${entityId}${unlistedHash ? `:${unlistedHash}` : ''}`;
      const entityType = entityData['entity_type'];
      const baseUrl = entityData['base_url'];
      const hasNewLeadCaptureFlow =
        entityData['has_new_lead_capture_flow'] || entityData['has_registration_enabled'];

      if (entityType === ENTITY_TYPE.EVENT && !eventId) {
        reportErrorToPlayer({
          message: `Event id is missing for entity type: ${entityType}`,
          code: ERROR_CODES.EVENT_ID_MISSING,
        });
        return setErrored(true);
      }

      if (entityId && entityType && baseUrl) {
        if (
          entityId.length > 0 &&
          Object.values(ENTITY_TYPE).includes(entityType) &&
          baseUrl.length > 0
        ) {
          useGlobalStore.setState({
            entityId,
            entityType,
            baseUrl,
            hasNewLeadCaptureFlow,
            eventId,
            apiEventId,
            apiEntityId,
            unlistedHash,
          });
          setValidated(true);
        } else {
          reportErrorToPlayer({
            message: `Invalid entity data: ${entityId}, ${entityType}, ${baseUrl}`,
            code: ERROR_CODES.INVALID_ENTITY_TYPE,
          });
          setErrored(true);
        }
      } else {
        reportErrorToPlayer({
          message: `Entity data is missing: ${entityId}, ${entityType}, ${baseUrl}`,
          code: ERROR_CODES.ENTITY_DATA_MISSING,
        });
        setErrored(true);
      }
    }, [entityData, router.query, router.isReady]);

    if (errored) {
      return <ValidationError>Error: Lead Capture Validation Failed</ValidationError>;
    }

    if (!validated) {
      return <></>;
    }

    return <WrappedComponent {...props} />;
  };
}

const ValidationError = styled.div`
  color: green;
`;

export default withValidation;
